import React from 'react';
import { useTreatmentTypes } from '../hooks/useTreatmentTypes';
import { getCurrentLocale } from '../services/api';

const TreatmentTypesSection: React.FC = () => {
  const { data, loading } = useTreatmentTypes();
  const locale = getCurrentLocale();

  // Hardcoded intro text based on the provided Dutch text
  const hardcodedIntro = {
    title: locale === 'ar' ? 'علاجاتنا المتخصصة' : 'Onze behandelingen',
    description: locale === 'ar' 
      ? 'يتم حقن العلاجات ضد تساقط الشعر عن طريق تحفيز نمو الشعر الطبيعي، دون الحاجة لعملية جراحية. تتم العلاجات دون تخدير وتحفز نمو الشعر الطبيعي دون آثار جانبية. أثناء العلاج يتم حقن سلسلة من الحقن الدقيقة تحت الجلد. يتم حقن المكونات المكونة من محلول طبي على أساس الأحماض الأمينية المتقدمة والفيتامينات والمعادن لتحفيز نمو الشعر.'
      : 'De behandelingen worden ingezet tegen haarverlies door het stimuleren van uw eigen natuurlijke haargroei, zonder dat daar een operatie voor nodig is. De behandelingen nemen dus het haarverlies weg zonder bijwerkingen en stimuleren nieuwe haargroei.<br/><br/>Tijdens de behandeling worden een serie micro injecties net onder de huid ingebracht. De ingrediënten worden geïnjecteerd bestaand uit een medische oplossing op basis van geavanceerde aminozuren, vitaminen en mineralen om tegen te gaan en haargroei te stimuleren. De unieke formule grijpt direct in op de haarwortelcellen en biedt ondersteuning bij verschillende vormen van niet-erfelijke alopecia. Daarbij helpt het product bij haarverlies door interne of externe factoren zoals voedingstekort en stress.'
  };

  // Default content when no Strapi data is available
  const defaultData = {
    title: hardcodedIntro.title,
    description: hardcodedIntro.description,
    treatmentCards: [
      {
        id: 1,
        title: locale === 'ar' ? 'فروة الرأس' : 'Hoofdhuid',
        description: locale === 'ar' 
          ? 'تساقط الشعر، الشعر الخفيف أو الباهت. اليوم هذا لا ينطبق فقط على الرجال ولكن بالتأكيد أيضًا على النساء.'
          : 'Haaruitval, dunner wordend of dof haar. Tegenwoordig geldt dit niet alleen voor heren maar zeker ook voor dames.',
        features: locale === 'ar' 
          ? ['تساقط الشعر الوراثي', 'التغيرات الهرمونية', 'تساقط الشعر الناتج عن التوتر']
          : ['Erfelijk haarverlies', 'Hormonale veranderingen', 'Stressgerelateerd haarverlies'],
        price: locale === 'ar' ? 'اقرأ المزيد' : 'Lees meer',
      },
      {
        id: 2,
        title: locale === 'ar' ? 'لحية' : 'Baard',
        description: locale === 'ar' 
          ? 'في مناطق اللحية يمكن أن تظهر بقع صلعاء. هنا أيضًا يمكننا تحسين نمو الشعر.'
          : 'In de baardzones kunnen kale plekjes ontstaan. Ook hier kunnen wij haargroei verbeteren.',
        features: locale === 'ar' 
          ? ['نمو لحية غير مكتمل', 'فراغات', 'بطء في نمو شعر اللحية']
          : ['Patchy baardgroei', 'Kale plekken', 'Traag groeiende baardharen'],
        price: locale === 'ar' ? 'اقرأ المزيد' : 'Lees meer',
      },
      {
        id: 3,
        title: locale === 'ar' ? 'شارب' : 'Snor',
        description: locale === 'ar' 
          ? 'عندما تريد جعل شعر الشارب أقوى وأكثر كثافة يمكننا مساعدتك على تحسين نمو الشعر.'
          : 'Wanneer je je snorharen steviger en voller wil maken kunnen wij je helpen om de haargroei te verbeteren.',
        features: locale === 'ar' 
          ? ['نمو شارب خفيف أو غير منتظم']
          : ['Dunne of onregelmatige snorgroei'],
        price: locale === 'ar' ? 'اقرأ المزيد' : 'Lees meer',
      },
    ],
  };

  if (loading) {
    return (
      <section className="price-list-section">
        <div className="loading-placeholder">
          <div className="loading-title">Loading...</div>
        </div>
      </section>
    );
  }

  // Merge Strapi data with sensible locale-specific defaults when fields are missing
  const title = hardcodedIntro.title;
  const description = hardcodedIntro.description;
  const cardsFromApi = Array.isArray(data) ? data : [];
  const sourceCards = cardsFromApi.length > 0 ? cardsFromApi : defaultData.treatmentCards;
  const mergedCards = sourceCards.map((card, index) => {
    const defaultCard = defaultData.treatmentCards[index] || {} as any;
    
    // For API data, use ingredients as features, fallback to suitableFor, for default data use features directly
    let features: string[] = [];
    if (Array.isArray(card.ingredients) && card.ingredients.length > 0) {
      // Use ingredients from API data
      features = card.ingredients.map((ingredient: any) => ingredient.text).filter(Boolean);
    } else if (card.suitableFor) {
      // Split suitableFor by comma and clean up
      features = card.suitableFor.split(',').map((f: string) => f.trim()).filter(Boolean);
    } else if (Array.isArray(card.features)) {
      features = card.features;
    } else if (defaultCard.features) {
      features = defaultCard.features;
    }

    const price = card.ctaButton?.text || defaultCard.price || (locale === 'ar' ? 'اقرأ المزيد' : 'Lees meer');
    const mergedTitle = card.name || defaultCard.title;
    const mergedDescription = card.shortDescription || card.description || defaultCard.description;
    
    return {
      id: card.id ?? defaultCard.id ?? index,
      title: mergedTitle,
      description: mergedDescription,
      features,
      price,
    };
  });

  return (
    <section className="price-list-section">
      <div className="price-list-container">
        <h2 className="section-title">{title}</h2>
        
        {description && (
          <p 
            className="section-subtitle" 
            style={{ maxWidth: '100%', width: '100%' }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        <div className="price-cards-grid">
          {mergedCards.map((card) => (
            <div key={card.id} className="price-card">
              <h4 className="card-title-price">{card.title}</h4>
              {card.description && (
                <div 
                  className="package-description"
                  dangerouslySetInnerHTML={{ __html: card.description }}
                />
              )}
              <ul className="features-list">
                {(card.features || []).map((feature, index) => {
                  const text = typeof feature === 'string' ? feature : (feature as any)?.text;
                  if (!text) return null;
                  return (
                    <li key={index} className="feature-item">
                      {text}
                    </li>
                  );
                })}
              </ul>
              <div className="card-price">{card.price}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TreatmentTypesSection;